import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  Cell,
  ScatterChart,
  Scatter,
  ZAxis,
  ReferenceLine
} from 'recharts'
import type { ReportFilters } from '@/lib/reports-engine'
import { useProjectReports } from '@/hooks/useReportsEngine'
import { TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { useProfile } from '@/hooks/useProfile'

interface ProfitabilityReportTabProps {
  filters: ReportFilters
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 })

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium mb-2">{data.name}</p>
        <div className="space-y-1 text-muted-foreground">
          <p>Receita: <span className="text-foreground font-medium">{formatCurrency(data.revenue)}</span></p>
          <p>Horas: <span className="text-foreground font-medium">{data.hoursSpent}h</span></p>
          <p>Taxa efetiva: <span className={cn("font-medium", data.isProfitable ? "text-success" : "text-destructive")}>
            {formatCurrency(data.effectiveRate)}/h
          </span></p>
          <p>Margem: <span className={cn("font-medium", data.marginPercent >= 0 ? "text-success" : "text-destructive")}>
            {data.marginPercent >= 0 ? '+' : ''}{data.marginPercent}%
          </span></p>
        </div>
      </div>
    )
  }
  return null
}

export function ProfitabilityReportTab({ filters }: ProfitabilityReportTabProps) {
  const { data: projects, isLoading } = useProjectReports(filters)
  const { data: profile } = useProfile()

  const targetRate = profile?.hourly_rate || 80

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[300px] rounded-xl" />
        <Skeleton className="h-[200px] rounded-xl" />
      </div>
    )
  }

  // Sort by revenue for bar chart
  const sortedByRevenue = [...(projects || [])].sort((a, b) => b.revenue - a.revenue).slice(0, 10)
  
  // Prepare scatter data for rate vs hours
  const scatterData = (projects || [])
    .filter(p => p.hoursSpent > 0)
    .map(p => ({
      ...p,
      x: p.hoursSpent,
      y: p.effectiveRate,
      z: p.revenue,
      color: p.isProfitable ? 'hsl(var(--success))' : 'hsl(var(--destructive))'
    }))

  const profitableCount = (projects || []).filter(p => p.isProfitable).length
  const unprofitableCount = (projects || []).filter(p => !p.isProfitable && p.hoursSpent > 0).length
  const avgMargin = projects?.length 
    ? projects.reduce((sum, p) => sum + p.marginPercent, 0) / projects.length 
    : 0

  return (
    <div className="space-y-6">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-3">
        <Badge variant="outline" className="bg-success/10 text-success border-success/20 px-3 py-1.5">
          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
          {profitableCount} projetos rentáveis
        </Badge>
        {unprofitableCount > 0 && (
          <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 px-3 py-1.5">
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
            {unprofitableCount} abaixo da meta
          </Badge>
        )}
        <Badge variant="outline" className="px-3 py-1.5">
          Margem média: <span className={cn("ml-1 font-semibold", avgMargin >= 0 ? "text-success" : "text-destructive")}>
            {avgMargin >= 0 ? '+' : ''}{avgMargin.toFixed(0)}%
          </span>
        </Badge>
      </div>

      {/* Revenue by project chart */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Receita por Projeto
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sortedByRevenue.length > 0 ? (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sortedByRevenue} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <XAxis 
                    type="number" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `R$${(value / 1000).toFixed(0)}k`}
                  />
                  <YAxis 
                    type="category" 
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    width={120}
                    tickFormatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" radius={[0, 4, 4, 0]} barSize={20}>
                    {sortedByRevenue.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isProfitable ? 'hsl(var(--success))' : 'hsl(var(--destructive))'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[280px] flex items-center justify-center">
              <p className="text-muted-foreground">Sem dados de projetos no período</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scatter chart - Rate vs Hours */}
      {scatterData.length > 0 && (
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Taxa Efetiva vs Horas Trabalhadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                  <XAxis 
                    type="number" 
                    dataKey="x" 
                    name="Horas"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    label={{ value: 'Horas', position: 'bottom', offset: 0, fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="y" 
                    name="Taxa/h"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    tickFormatter={(value) => `R$${value}`}
                    label={{ value: 'Taxa/h', angle: -90, position: 'insideLeft', fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <ZAxis type="number" dataKey="z" range={[50, 400]} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine 
                    y={targetRate} 
                    stroke="hsl(var(--primary))" 
                    strokeDasharray="5 5" 
                    label={{ value: `Meta: R$${targetRate}/h`, position: 'right', fontSize: 10, fill: 'hsl(var(--primary))' }}
                  />
                  <Scatter data={scatterData}>
                    {scatterData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects table */}
      {(projects || []).length > 0 && (
        <Card className="shadow-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Detalhamento por Projeto</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Projeto</th>
                    <th className="pb-2 font-medium text-muted-foreground">Cliente</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Horas</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Receita</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Taxa/h</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right">Margem</th>
                  </tr>
                </thead>
                <tbody>
                  {projects?.slice(0, 10).map((project) => (
                    <tr key={project.id} className="border-b border-dashed last:border-0">
                      <td className="py-2 font-medium">{project.name}</td>
                      <td className="py-2 text-muted-foreground">{project.client}</td>
                      <td className="py-2 text-right">{project.hoursSpent}h</td>
                      <td className="py-2 text-right">{formatCurrency(project.revenue)}</td>
                      <td className={cn("py-2 text-right font-medium", project.isProfitable ? "text-success" : "text-destructive")}>
                        {formatCurrency(project.effectiveRate)}/h
                      </td>
                      <td className={cn("py-2 text-right font-medium", project.marginPercent >= 0 ? "text-success" : "text-destructive")}>
                        {project.marginPercent >= 0 ? '+' : ''}{project.marginPercent}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
